import mSpine from '../deps';
import {
  CHARACTER_ANIMATION_ATLAS,
  CHARACTER_ANIMATION_JSON,
  CharacterAnimations,
} from 'shared/constants/characterAnimations';
import { getRandom } from 'shared/helpers/random';

export interface PlayerListener {
  onReady(): void;
  onAnimationStart(animation: Animation): void;
}

export enum Animation {
  ENCOURAGE = 'Encourage',
  IDLE_1 = 'Idle1',
  IDLE_2 = 'Idle2',
  WIN = 'Win',
  LOSE = 'Lose',
  SLEEPY = 'Sleeppy',
  WAKE_UP = 'WakeUp',
  YAWN_TO_IDLE_1 = 'YawnToIdle1',
  YAWN_TO_SLEEPY = 'YawnToSleepy',
}

const playerWrapper = () => {
  const listeners: PlayerListener[] = [];
  const minEncourageAnimationDelayMillis = 20000;
  const maxEncourageAnimationDelayMillis = 30000;
  const encourageAnimationDelayAfterStart = 5000;

  //game not started
  const idlesToIdle2 = 4;
  const idlesToSleep = 8;

  //game started
  const idlesToYawnToIdle = 3;

  let isAttached = false;
  let player: any = null;
  let ready = false;
  let error = false;
  let isGameStarted = false;
  let animationState: any = null;
  let nextEncourageAnimationTime = 0;
  let isSleeping = false;
  let idleCount = 0;
  let currentTrack: any = null;

  const animationListener = {
    /** Invoked when this entry has been set as the current entry. */
    start: (entry: any) => {
      currentTrack = entry;
    },

    /** Invoked when another entry has replaced this entry as the current entry. This entry may continue being applied for
     * mixing. */
    interrupt: (entry: any) => {},

    /** Invoked when this entry is no longer the current entry and will never be applied again. */
    end: (entry: any) => {},

    /** Invoked when this entry will be disposed. This may occur without the entry ever being set as the current entry.
     * References to the entry should not be kept after dispose is called, as it may be destroyed or reused. */
    dispose: (entry: any) => {},

    /** Invoked every time this entry's animation completes a loop. */
    complete: (entry: any) => {
      try {
        if (entry && entry.loop) {
          if (isGameStarted) {
            idleCount++;

            if (idleCount >= idlesToYawnToIdle) {
              player.setAnimation(CharacterAnimations.YAWN_TO_IDLE_1, false);
              notifyAnimationStart(Animation.YAWN_TO_IDLE_1);
            }
            return;
          }

          if (!isSleeping) {
            idleCount++;

            if (idleCount === idlesToIdle2) {
              player.setAnimation(CharacterAnimations.IDLE_2, true);
              notifyAnimationStart(Animation.IDLE_2);
              return;
            }

            if (idleCount >= idlesToSleep) {
              idleCount = 0;
              isSleeping = true;
              player.setAnimation(CharacterAnimations.YAWN_TO_SLEEPY, false);
              notifyAnimationStart(Animation.YAWN_TO_SLEEPY);
              return;
            }

            return;
          }
        }

        if (currentTrack !== entry) {
          return;
        }

        if (isSleeping) {
          player.setAnimation(CharacterAnimations.SLEEPY, true);
          notifyAnimationStart(Animation.SLEEPY);
          return;
        }

        player.setAnimation(CharacterAnimations.IDLE_1, true);
        notifyAnimationStart(Animation.IDLE_1);

        idleCount = 0;
        isSleeping = false;
      } catch (e) {}
    },

    /** Invoked when this entry's animation triggers an event. */
    event: (entry: any) => {},
  };

  const spineConfig = {
    jsonUrl: CHARACTER_ANIMATION_JSON,
    atlasUrl: CHARACTER_ANIMATION_ATLAS,
    animation: CharacterAnimations.IDLE_1,
    alpha: true,
    premultipliedAlpha: false,
    showControls: false,
    showLoading: false,
    viewport: {
      x: -100,
      y: -480,
      width: 560,
      height: 480,
      padLeft: 0,
      padRight: 0,
      padTop: 0,
      padBottom: 10,
    },
    success: (spinePlayer: any) => {
      ready = true;
      if (animationState) {
        animationState.removeListener(animationListener);
      }
      animationState = player.animationState;
      if (animationState) {
        animationState.addListener(animationListener);
      }
      listeners.forEach(l => l.onReady());
    },
    error: (spinePlayer: any, msg: string) => {
      ready = true;
      error = true;
      listeners.forEach(l => l.onReady());
      detach();
    },
  };

  const refreshEncourageAnimationTime = (from: number) => {
    nextEncourageAnimationTime =
      from +
      getRandom(
        minEncourageAnimationDelayMillis,
        maxEncourageAnimationDelayMillis,
      );
  };

  const setGameStarted = (gameStarted: boolean) => {
    isGameStarted = gameStarted;
    idleCount = 0;

    if (!gameStarted) {
      return;
    }

    const now = Date.now();

    if (nextEncourageAnimationTime === 0) {
      refreshEncourageAnimationTime(now);
      return;
    }

    if (now > nextEncourageAnimationTime) {
      nextEncourageAnimationTime =
        Date.now() + encourageAnimationDelayAfterStart;
    }
  };

  const wakeUp = () => {
    if (isSleeping) {
      idleCount = 0;
      isSleeping = false;
      player.setAnimation(CharacterAnimations.WAKE_UP, false);
      notifyAnimationStart(Animation.WAKE_UP);
      return true;
    }

    return false;
  };

  const notifyAnimationStart = (animation: Animation) => {
    if (!ready || !isAttached) {
      return;
    }
    listeners.forEach(l => l.onAnimationStart(animation));
  };

  const playAnimation = (animation: Animation): boolean => {
    if (!isAttached) {
      return false;
    }

    switch (animation) {
      case Animation.LOSE:
        player.setAnimation(CharacterAnimations.LOSE, false);
        notifyAnimationStart(Animation.LOSE);
        break;
      case Animation.WIN:
        player.setAnimation(CharacterAnimations.WIN, false);
        notifyAnimationStart(Animation.WIN);
        break;
      case Animation.ENCOURAGE:
        if (wakeUp()) {
          return false;
        }
        const now = Date.now();
        if (now < nextEncourageAnimationTime) {
          idleCount = 0;
          return false;
        }
        refreshEncourageAnimationTime(now);
        player.setAnimation(CharacterAnimations.ENCOURAGE, false);
        notifyAnimationStart(Animation.ENCOURAGE);
        break;
      case Animation.WAKE_UP:
        if (!wakeUp()) {
          return false;
        }
        break;
    }

    idleCount = 0;
    isSleeping = false;
    return true;
  };

  const resetIdleCount = () => {
    idleCount = 0;
  };

  const subscribeListener = (listener: PlayerListener) => {
    if (listeners.indexOf(listener) >= 0) {
      return;
    }
    listeners.push(listener);
    if (ready) {
      listener.onReady();
    }
  };

  const unsubscribeListener = (listener: PlayerListener) => {
    listeners.filter(l => l !== listener);
  };

  const attach = (parent: string | HTMLElement) => {
    if (isAttached) {
      detach();
    }

    if (error) {
      return;
    }

    const mParent =
      typeof parent === 'string' ? document.getElementById(parent) : parent;

    if (player === null) {
      player = new mSpine.SpinePlayer(mParent, spineConfig);
    } else {
      player.parent = mParent;
      player.parent.appendChild(player.dom);
      player.play();
    }

    isAttached = true;
  };

  const detach = () => {
    if (player === null || !isAttached) {
      return;
    }
    player.pause();
    player.parent.removeChild(player.dom);
    isAttached = false;
  };

  return {
    attach,
    detach,
    subscribeListener,
    unsubscribeListener,
    playAnimation,
    resetIdleCount,
    setGameStarted,
  };
};

const mPlayerWrapper = playerWrapper();
mPlayerWrapper.attach(document.createElement('div'));
export default mPlayerWrapper;
